/**
 * Vardiyalar Sayfası — BOSS.
 * Tab 1: Haftalık Planlama (Faz 2.2)
 * Tab 2: Kalıplar (Faz 2.1)
 */

import { useEffect, useState, useMemo } from 'react'
import {
  Plus, Pencil, Trash2, RotateCcw, Clock, Building2, Calendar,
} from 'lucide-react'
import {
  tumKaliplariGetir,
  kalipSil,
  kalipGeriYukle,
  kalipSaati,
  type VardiyaKalibi,
} from '@/lib/services/vardiya-kalibi-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { VardiyaKalibiFormModal } from './VardiyaKalibiFormModal'
import { HaftalikPlanlama } from './HaftalikPlanlama'
import type { Sube, Pozisyon } from '@/types'

type Tab = 'planlama' | 'kaliplar'

export function VardiyalarPage() {
  const [tab, setTab] = useState<Tab>('planlama')
  const [kaliplar, setKaliplar] = useState<VardiyaKalibi[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [pasifGoster, setPasifGoster] = useState(false)

  const [formAcik, setFormAcik] = useState(false)
  const [duzenlenecek, setDuzenlenecek] = useState<VardiyaKalibi | null>(null)
  const [silinecek, setSilinecek] = useState<VardiyaKalibi | null>(null)

  async function yukle() {
    try {
      setYukleniyor(true)
      const [k, s, p] = await Promise.all([
        tumKaliplariGetir(),
        tumSubeleriGetir(),
        tumPozisyonlariGetir(),
      ])
      setKaliplar(k)
      setSubeler(s.filter((x) => x.aktif))
      setPozisyonlar(p.filter((x) => x.aktif))
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Veriler yüklenemedi.')
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
  }, [])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  const pozisyonHaritasi = useMemo(() => {
    const m = new Map<string, Pozisyon>()
    pozisyonlar.forEach((p) => m.set(p.id, p))
    return m
  }, [pozisyonlar])

  const subeyeGoreKaliplar = useMemo(() => {
    const harita = new Map<string, VardiyaKalibi[]>()
    const filtrelenenler = pasifGoster ? kaliplar : kaliplar.filter((k) => k.aktif)
    filtrelenenler.forEach((k) => {
      const liste = harita.get(k.subeId) ?? []
      liste.push(k)
      harita.set(k.subeId, liste)
    })
    return harita
  }, [kaliplar, pasifGoster])

  async function silOnay() {
    if (!silinecek) return
    try {
      await kalipSil(silinecek.id)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  async function geriYukle(k: VardiyaKalibi) {
    try {
      await kalipGeriYukle(k.id)
      await yukle()
    } catch (err) {
      alert('Geri yüklenemedi: ' + (err as Error).message)
    }
  }

  const subeYok = subeler.length === 0
  const aktifKalipSayisi = kaliplar.filter((k) => k.aktif).length

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vardiyalar</h1>
          <p className="text-sm text-gray-600 mt-1">
            Vardiya kalıpları + haftalık planlama
          </p>
        </div>
        {tab === 'kaliplar' && (
          <button
            onClick={() => {
              setDuzenlenecek(null)
              setFormAcik(true)
            }}
            className="btn-primary flex items-center gap-2"
            disabled={subeYok}
            title={subeYok ? 'Önce şube ekle' : ''}
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Yeni Kalıp</span>
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setTab('planlama')}
          className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition-colors ${
            tab === 'planlama'
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Calendar className="w-4 h-4 inline mr-1.5" />
          Haftalık Planlama
        </button>
        <button
          onClick={() => setTab('kaliplar')}
          className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm transition-colors ${
            tab === 'kaliplar'
              ? 'border-brand-600 text-brand-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1.5" />
          Kalıplar ({aktifKalipSayisi})
        </button>
      </div>

      {tab === 'planlama' && (
        <>
          {yukleniyor ? (
            <div className="card text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
              <p className="text-gray-600">Yükleniyor...</p>
            </div>
          ) : (
            <HaftalikPlanlama subeler={subeler} />
          )}
        </>
      )}

      {tab === 'kaliplar' && (
        <>
          {hata && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-4">
              {hata}
            </div>
          )}

          {subeYok && !yukleniyor && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 mb-4">
              <p className="font-semibold mb-1">⚠️ Önce şube ekle</p>
              <p className="text-sm">
                Vardiya kalıpları şubeye bağlıdır. Önce en az 1 şube oluştur.
              </p>
            </div>
          )}

          {yukleniyor && (
            <div className="card text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
              <p className="text-gray-600">Yükleniyor...</p>
            </div>
          )}

          {!yukleniyor && !subeYok && aktifKalipSayisi === 0 && !pasifGoster && (
            <div className="card text-center py-12">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-bold text-gray-900 mb-2">Henüz kalıp yok</h3>
              <p className="text-gray-600 mb-6">
                İlk kalıbı eklemek için yukarıdaki butonu kullan.
              </p>
              <p className="text-xs text-gray-400 mb-4">
                Örnek: "Sabah Kasiyer" (08:00-18:00), "Gece Rafçı" (22:00-08:00)
              </p>
              <button
                onClick={() => {
                  setDuzenlenecek(null)
                  setFormAcik(true)
                }}
                className="btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                İlk Kalıbı Ekle
              </button>
            </div>
          )}

          {!yukleniyor && aktifKalipSayisi > 0 && (
            <>
              {kaliplar.some((k) => !k.aktif) && (
                <div className="mb-4 text-right">
                  <button
                    onClick={() => setPasifGoster(!pasifGoster)}
                    className="text-sm text-brand-600 hover:underline"
                  >
                    {pasifGoster ? 'Sadece aktifleri göster' : 'Pasifleri de göster'}
                  </button>
                </div>
              )}

              <div className="space-y-6">
                {Array.from(subeyeGoreKaliplar.entries()).map(([subeId, liste]) => {
                  const sube = subeHaritasi.get(subeId)
                  return (
                    <div key={subeId}>
                      <div className="flex items-center gap-2 mb-3">
                        <Building2 className="w-5 h-5 text-gray-500" />
                        <h2 className="font-bold text-gray-900">
                          {sube?.isim ?? 'Bilinmeyen Şube'}
                        </h2>
                        <span className="text-sm text-gray-500">
                          ({liste.length} kalıp)
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {liste.map((k) => {
                          const sure = kalipSaati(k)
                          const pozisyon = k.pozisyonId
                            ? pozisyonHaritasi.get(k.pozisyonId)
                            : null
                          const gece = k.bitis.localeCompare(k.baslangic) <= 0
                          return (
                            <div key={k.id} className={`card ${!k.aktif ? 'opacity-60' : ''}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <h3 className="font-bold text-gray-900 truncate">{k.isim}</h3>
                                    {!k.aktif && (
                                      <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                                        Pasif
                                      </span>
                                    )}
                                    {gece && (
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                        🌙 Gece
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mb-1">
                                    <Clock className="w-3 h-3 inline mr-1" />
                                    {k.baslangic} – {k.bitis}{' '}
                                    <span className="text-xs text-gray-500">({sure} saat)</span>
                                  </p>
                                  {pozisyon && (
                                    <p className="text-xs text-gray-500">
                                      Pozisyon: {pozisyon.isim}
                                    </p>
                                  )}
                                </div>

                                <div className="flex flex-col gap-1">
                                  {k.aktif ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          setDuzenlenecek(k)
                                          setFormAcik(true)
                                        }}
                                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
                                        title="Düzenle"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => setSilinecek(k)}
                                        className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
                                        title="Pasif Yap"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() => geriYukle(k)}
                                      className="p-2 hover:bg-green-50 rounded-lg text-gray-600 hover:text-green-600 transition-colors"
                                      title="Geri Yükle"
                                    >
                                      <RotateCcw className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      <VardiyaKalibiFormModal
        acik={formAcik}
        kalip={duzenlenecek}
        subeler={subeler}
        pozisyonlar={pozisyonlar}
        onKapat={() => setFormAcik(false)}
        onKaydedildi={() => {
          setFormAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Kalıbı Pasif Yap"
        mesaj={`"${silinecek?.isim}" kalıbı pasif olacak. Bu kalıba atanmış geçmiş vardiyalar korunur. Devam mı?`}
        onayMetni="Pasif Yap"
        tehlikeli
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />
    </div>
  )
}
